import React, { ComponentType, useEffect } from "react";
import { Route, Redirect, useLocation } from "react-router-dom";
import Layout from "../components/Layout/Layout";
import AuthenticationService from "../_services/AuthenticationService";
import PageGuard from "../access-control/PageGuard";
import { RouteSpec } from "./types";

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const currentUser = AuthenticationService.currentUserValue;
  if (currentUser) {
    return <>{children}</>;
  }
  if (new URLSearchParams(location.search).get("token")) {
    return null;
  }
  const next = encodeURIComponent(location.pathname + location.search);
  return <Redirect to={`/unauthorized?reason=not-logged-in&next=${next}`} />;
};

function compose(spec: RouteSpec, Inner: ComponentType<any>): ComponentType<any> {
  let Composed: ComponentType<any> = Inner;

  if (spec.pageKey) {
    const Guarded: React.FC<any> = (p) => (
      <PageGuard pageKey={spec.pageKey!} restrictedByDefault={spec.restrictedByDefault}>
        <Inner {...p} />
      </PageGuard>
    );
    Composed = Guarded;
  }

  if (spec.layout === "default") {
    const Inner2 = Composed;
    const Layouted: React.FC<any> = (p) => (
      <Layout>
        <Inner2 {...p} />
      </Layout>
    );
    Composed = Layouted;
  }

  if (spec.auth === "jwt" || spec.auth === "jwt-role") {
    const Inner3 = Composed;
    const Authed: React.FC<any> = (p) => (
      <RequireAuth>
        <Inner3 {...p} />
      </RequireAuth>
    );
    Composed = Authed;
  }

  return Composed;
}

/**
 * Build the route elements as a flat array (NOT wrapped in a fragment) so they
 * can be rendered as DIRECT children of a <Switch>. This gives true first-match
 * behavior and, crucially, lets a catch-all fallback <Route> be appended after
 * them (see routes/index.tsx) — otherwise an unregistered/mistyped path renders
 * a blank screen instead of a 404.
 */
export function renderRoutes(routes: RouteSpec[]): React.ReactElement[] {
  return routes.map((spec, i) => {
    const Composed = compose(spec, spec.component);
    return (
      <Route
        path={spec.path}
        exact={spec.exact}
        key={`${Array.isArray(spec.path) ? spec.path.join("|") : spec.path}-${i}`}
        render={(routeProps) => <Composed {...routeProps} />}
      />
    );
  });
}

interface RenderProps {
  routes: RouteSpec[];
}

export const RouteRenderer: React.FC<RenderProps> = ({ routes }) => {
  return <>{renderRoutes(routes)}</>;
};

export const ROUTE_TITLES = (routes: RouteSpec[]): Record<string, string> => {
  const out: Record<string, string> = {};
  routes.forEach((r) => {
    if (!r.title) return;
    const paths = Array.isArray(r.path) ? r.path : [r.path];
    paths.forEach((p) => {
      out[p] = r.title!;
    });
  });
  return out;
};

export const DocumentTitle: React.FC<{ titles: Record<string, string> }> = ({ titles }) => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    let title = titles[path];
    if (!title) {
      const match = Object.keys(titles)
        .filter((k) => path.startsWith(k + "/") || path === k)
        .sort((a, b) => b.length - a.length)[0];
      if (match) title = titles[match];
    }
    document.title = title ? `${title} | EduSquares` : "EduSquares";
  }, [location.pathname, titles]);
  return null;
};
